'use client';

import * as React from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * FloatingOrbs — Schwebende 3D-Glaskugeln
 *
 * Technik: CSS perspective + translateZ + framer-motion float animations
 * CPU-Impact: Very Low (GPU-composited CSS transforms)
 *
 * Verwendung:
 *   <FloatingOrbsBackground>
 *     <div className="relative z-10">Content</div>
 *   </FloatingOrbsBackground>
 */
const DARK_COLORS = [
  ['#06b6d4', '#0284c7'],
  ['#8b5cf6', '#6d28d9'],
  ['#10b981', '#059669'],
  ['#ec4899', '#db2777'],
  ['#f59e0b', '#d97706'],
  ['#3b82f6', '#1d4ed8'],
];
const LIGHT_COLORS = [
  ['#0ea5e9', '#0369a1'],
  ['#a855f7', '#7e22ce'],
  ['#34d399', '#059669'],
  ['#f472b6', '#be185d'],
  ['#fbbf24', '#b45309'],
  ['#60a5fa', '#1e40af'],
];

function FloatingOrbsBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  interactive = true,
  orbCount = 12,
  colors,                   // auto from theme if not given
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const resolvedColors = colors ?? (isDark ? DARK_COLORS : LIGHT_COLORS);
  const bgClass = isDark
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
    : 'bg-gradient-to-br from-slate-50 via-white to-slate-50';
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 100, damping: 25 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 25 });

  const orbs = React.useMemo(() => (
    Array.from({ length: orbCount }, (_, i) => {
      const colorPair = resolvedColors[i % resolvedColors.length];
      const depth = Math.random(); // 0 = far, 1 = near
      return {
        id: i,
        x: 5 + Math.random() * 90,
        y: 5 + Math.random() * 90,
        size: 40 + depth * 120,          // near orbs are bigger
        z: depth,
        color1: colorPair[0],
        color2: colorPair[1],
        floatAmp: 8 + depth * 20,         // near orbs float more
        floatDur: (4 + Math.random() * 6) / speed,
        floatDelay: -Math.random() * 8,
        blurBase: Math.max(0, (1 - depth) * 8), // far orbs slightly blurred
        opacityBase: 0.15 + depth * 0.25,
        parallaxStrength: 0.3 + depth * 0.7,
      };
    })
  ), [orbCount, resolvedColors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseMove = React.useCallback((e) => {
    if (!interactive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    mouseX.set(((e.clientX - rect.left - cx) / cx) * 20);
    mouseY.set(((e.clientY - rect.top - cy) / cy) * 20);
  }, [interactive, mouseX, mouseY]);

  const handleMouseLeave = React.useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  return (
    <div
      data-slot="floating-orbs-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      style={{ perspective: '1200px' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ opacity }}>
        {orbs.map((orb) => (
          <OrbItem
            key={orb.id}
            orb={orb}
            springX={springX}
            springY={springY}
          />
        ))}
      </div>

      {/* Subtle center glow */}
      <div className="absolute inset-0 bg-radial from-white/[0.03] via-transparent to-transparent pointer-events-none" />

      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

function OrbItem({ orb, springX, springY }) {
  const px = useSpring(springX, { stiffness: 50 + orb.z * 100, damping: 20 });
  const py = useSpring(springY, { stiffness: 50 + orb.z * 100, damping: 20 });

  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        left: `${orb.x}%`,
        top: `${orb.y}%`,
        width: orb.size,
        height: orb.size,
        translateX: '-50%',
        translateY: '-50%',
        background: `radial-gradient(circle at 35% 35%, ${orb.color1}cc, ${orb.color2}44)`,
        filter: `blur(${orb.blurBase}px)`,
        boxShadow: `0 0 ${orb.size * 0.4}px ${orb.color1}33, inset 0 0 ${orb.size * 0.3}px ${orb.color2}22`,
        border: `1px solid ${orb.color1}22`,
        x: px.get() * orb.parallaxStrength,
        y: py.get() * orb.parallaxStrength,
      }}
      animate={{
        y: [0, -orb.floatAmp, 0, orb.floatAmp * 0.5, 0],
        x: [0, orb.floatAmp * 0.3, 0, -orb.floatAmp * 0.4, 0],
        scale: [1, 1.05, 1, 0.97, 1],
        opacity: [orb.opacityBase, orb.opacityBase * 1.4, orb.opacityBase, orb.opacityBase * 1.2, orb.opacityBase],
      }}
      transition={{
        duration: orb.floatDur,
        delay: orb.floatDelay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

export { FloatingOrbsBackground };
